<template>
  <v-app class="delivery-note-sign">
    <div class="delivery-note-sign__page">
      <div class="delivery-note-sign__shell">
        <div class="delivery-note-sign__panel">
          <div class="delivery-note-sign__topbar">
            <img
              class="delivery-note-sign__brand"
              :src="brandLogoSrc"
              alt="iTex+"
            />
          </div>

          <div class="delivery-note-sign__body">
            <div v-if="isLoading" class="delivery-note-sign__state">
              <v-progress-circular
                indeterminate
                color="green"
                size="54"
                width="4"
              ></v-progress-circular>
              <h1 class="delivery-note-sign__state-title">
                Lieferschein wird geladen
              </h1>
              <p class="delivery-note-sign__state-text">
                Die Signaturseite wird vorbereitet.
              </p>
            </div>

            <div
              v-else-if="loadError"
              class="delivery-note-sign__state delivery-note-sign__state--error"
            >
              <div class="delivery-note-sign__state-icon">
                <span
                  class="delivery-note-sign__state-symbol delivery-note-sign__state-symbol--error"
                ></span>
              </div>
              <h1 class="delivery-note-sign__state-title">
                Link nicht verfügbar
              </h1>
              <p class="delivery-note-sign__state-text">
                {{ loadError }}
              </p>
            </div>

            <div
              v-else-if="successMessage"
              class="delivery-note-sign__state delivery-note-sign__state--success"
            >
              <div class="delivery-note-sign__state-icon">
                <span class="delivery-note-sign__state-symbol">
                  <span
                    class="delivery-note-sign__state-symbol delivery-note-sign__state-symbol--success"
                  ></span>
                </span>
              </div>
              <h1 class="delivery-note-sign__state-title">Vielen Dank</h1>
              <p class="delivery-note-sign__state-text">
                {{ successMessage }}
              </p>
            </div>

            <template v-else-if="deliveryNote">
              <div class="delivery-note-sign__hero">
                <div class="delivery-note-sign__hero-icon">
                  <i class="mdi mdi-file-document"></i>
                </div>
                <div class="delivery-note-sign__hero-copy">
                  <h1 class="delivery-note-sign__title">
                    Lieferschein online unterschreiben
                  </h1>
                  <p class="delivery-note-sign__subtitle">
                    Bitte prüfen Sie die Angaben und leisten Sie Ihre digitale
                    Unterschrift.
                  </p>
                </div>
              </div>

              <div class="delivery-note-sign__summary">
                <div class="delivery-note-sign__summary-item">
                  <div class="delivery-note-sign__summary-icon">
                    <i class="mdi mdi-clipboard-text"></i>
                  </div>
                  <div class="delivery-note-sign__summary-copy">
                    <span class="delivery-note-sign__summary-label">
                      Lieferschein-Nr.
                    </span>
                    <strong class="delivery-note-sign__summary-value">
                      {{ deliveryNote.note_number }}
                    </strong>
                  </div>
                </div>

                <div class="delivery-note-sign__summary-divider"></div>

                <div class="delivery-note-sign__summary-item">
                  <div class="delivery-note-sign__summary-icon">
                    <i class="mdi mdi-calendar"></i>
                  </div>
                  <div class="delivery-note-sign__summary-copy">
                    <span class="delivery-note-sign__summary-label">
                      Lieferscheindatum
                    </span>
                    <strong class="delivery-note-sign__summary-value">
                      {{ formatDateOnly(deliveryNote.delivery_note_date) }}
                    </strong>
                  </div>
                </div>

                <div class="delivery-note-sign__summary-divider"></div>

                <div
                  class="delivery-note-sign__summary-item delivery-note-sign__summary-item--customer"
                >
                  <div class="delivery-note-sign__summary-icon">
                    <i class="mdi mdi-account-multiple"></i>
                  </div>
                  <div class="delivery-note-sign__summary-copy">
                    <span class="delivery-note-sign__summary-label">
                      Kunde
                    </span>
                    <strong
                      class="delivery-note-sign__summary-value delivery-note-sign__summary-value--customer"
                    >
                      {{ deliveryNote.customer_name_snapshot }}
                    </strong>
                  </div>
                </div>
              </div>

              <section class="delivery-note-sign__section">
                <div class="delivery-note-sign__section-head">
                  <h2 class="delivery-note-sign__section-title">
                    Artikelübersicht
                  </h2>
                </div>

                <div class="delivery-note-sign__table">
                  <div class="delivery-note-sign__table-head">
                    <span>Artikel</span>
                    <span>Anzahl</span>
                  </div>

                  <div
                    v-for="item in deliveryNote.items"
                    :key="item.id"
                    class="delivery-note-sign__table-row"
                  >
                    <div
                      class="delivery-note-sign__table-cell delivery-note-sign__table-cell--article"
                    >
                      <span class="delivery-note-sign__item-icon">
                        <i class="mdi mdi-package-variant-closed"></i>
                      </span>
                      <span class="delivery-note-sign__item-copy">
                        <span class="delivery-note-sign__item-name">
                          {{ item.item_name }}
                        </span>
                        <span
                          v-if="item.external_item_id"
                          class="delivery-note-sign__item-number"
                        >
                          Art.-Nr. {{ item.external_item_id }}
                        </span>
                      </span>
                    </div>
                    <div
                      class="delivery-note-sign__table-cell delivery-note-sign__table-cell--quantity"
                    >
                      {{ item.quantity }}
                    </div>
                  </div>
                </div>
              </section>

              <section
                class="delivery-note-sign__section delivery-note-sign__section--signature"
              >
                <div class="delivery-note-sign__section-head">
                  <h2 class="delivery-note-sign__section-title">
                    Ihre Unterschrift
                  </h2>
                  <p class="delivery-note-sign__section-text">
                    Bitte unterschreiben Sie hier, um den Lieferschein
                    verbindlich zu bestätigen.
                  </p>
                </div>

                <div
                  class="delivery-note-sign__signature-surface"
                  :class="{
                    'delivery-note-sign__signature-surface--signed': !signatureEmpty
                  }"
                >
                  <div
                    v-if="signatureEmpty"
                    class="delivery-note-sign__signature-placeholder"
                  >
                    <i
                      class="mdi mdi-draw-pen delivery-note-sign__placeholder-icon"
                    ></i>
                    <strong>Bitte hier unterschreiben</strong>
                    <span>Mit Maus, Touch oder Finger</span>
                  </div>

                  <VueSignaturePad
                    ref="customerSignaturePad"
                    class="delivery-note-sign__signature-pad"
                    width="100%"
                    height="220px"
                    @mouseup.native="syncSignatureState"
                    @touchend.native="syncSignatureState"
                  />
                </div>
              </section>

              <div class="delivery-note-sign__action-dock">
                <p class="delivery-note-sign__confirmation-note">
                  Mit dem Absenden Ihrer Unterschrift bestätigen Sie die
                  Richtigkeit der Angaben.
                </p>

                <div class="delivery-note-sign__actions">
                  <v-btn
                    outlined
                    large
                    class="delivery-note-sign__clear-btn"
                    @click="clearSignature"
                  >
                    Zurücksetzen
                  </v-btn>

                  <v-btn
                    color="green"
                    elevation="2"
                    large
                    class="delivery-note-sign__submit-btn"
                    style="color: white;"
                    :loading="isSubmitting"
                    @click="submitSignature"
                  >
                    <span>Lieferschein bestätigen</span>
                    <v-icon
                      right
                      color="white"
                      class="delivery-note-sign__submit-icon"
                    >
                      mdi-check-circle-outline
                    </v-icon>
                  </v-btn>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </v-app>
</template>

<script>
import ApiService from "@/core/services/api.service";
import Swal from "sweetalert2";

export default {
  name: "DeliveryNoteSign",
  data() {
    return {
      isLoading: true,
      isSubmitting: false,
      deliveryNote: null,
      loadError: null,
      successMessage: null,
      signatureEmpty: true
    };
  },
  computed: {
    brandLogoSrc() {
      return `${process.env.BASE_URL}media/logos/itex-logo.png`;
    }
  },
  created() {
    this.fetchDeliveryNote();
  },
  methods: {
    fetchDeliveryNote() {
      this.isLoading = true;
      ApiService.get("delivery-note-sign", this.$route.params.token)
        .then(({ data }) => {
          this.deliveryNote = data.delivery_note;
          this.loadError = null;
          this.$nextTick(() => {
            this.prepareSignaturePad();
          });
        })
        .catch(error => {
          this.loadError =
            error?.response?.data?.message ||
            "Der Lieferschein-Link ist nicht mehr gültig.";
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    getSignaturePad() {
      const ref = this.$refs.customerSignaturePad;

      if (Array.isArray(ref)) {
        return ref[0] || null;
      }

      return ref || null;
    },
    prepareSignaturePad() {
      const signaturePad = this.getSignaturePad();

      if (!signaturePad) {
        return;
      }

      if (typeof signaturePad.resizeCanvas === "function") {
        signaturePad.resizeCanvas();
      }

      if (typeof signaturePad.clearSignature === "function") {
        signaturePad.clearSignature();
      }

      if (typeof signaturePad.openSignaturePad === "function") {
        signaturePad.openSignaturePad();
      }

      this.signatureEmpty = true;
    },
    syncSignatureState() {
      const signaturePad = this.getSignaturePad();
      if (!signaturePad || typeof signaturePad.isEmpty !== "function") {
        return;
      }

      this.signatureEmpty = signaturePad.isEmpty();
    },
    clearSignature() {
      const signaturePad = this.getSignaturePad();
      if (signaturePad && typeof signaturePad.clearSignature === "function") {
        signaturePad.clearSignature();
      }

      this.signatureEmpty = true;
    },
    formatDateOnly(value) {
      if (!value) {
        return "-";
      }

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }

      return date.toLocaleDateString("de-DE");
    },
    submitSignature() {
      const signaturePad = this.getSignaturePad();
      if (!signaturePad || typeof signaturePad.saveSignature !== "function") {
        Swal.fire({
          title: "Fehler",
          text: "Das Signaturfeld konnte nicht geladen werden.",
          icon: "error",
          confirmButtonText: "Ok"
        });
        return;
      }

      const { isEmpty, data } = signaturePad.saveSignature();
      if (isEmpty) {
        this.signatureEmpty = true;
        Swal.fire({
          title: "Fehlende Unterschrift",
          text:
            "Bitte unterschreiben Sie zuerst im vorgesehenen Feld, damit Sie den Lieferschein bestätigen können.",
          icon: "warning",
          confirmButtonText: "Ok"
        });
        return;
      }

      this.isSubmitting = true;
      ApiService.post(`delivery-note-sign/${this.$route.params.token}`, {
        signature: data
      })
        .then(() => {
          this.successMessage =
            "Ihre Unterschrift wurde gespeichert und der Lieferschein erfolgreich abgeschlossen.";
          this.deliveryNote = null;
        })
        .catch(error => {
          Swal.fire({
            title: "Fehler",
            text:
              error?.response?.data?.message ||
              "Die Unterschrift konnte nicht gespeichert werden.",
            icon: "error",
            confirmButtonText: "Ok"
          });
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    }
  }
};
</script>

<style scoped>
.delivery-note-sign {
  background: linear-gradient(180deg, #f4f7fb 0%, #eef3f8 100%);
}

.delivery-note-sign__page {
  min-height: 100vh;
  padding: 28px 18px;
}

.delivery-note-sign__shell {
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
}

.delivery-note-sign__panel {
  overflow: visible;
  border: 1px solid #e5ebf2;
  border-radius: 24px;
  background: #ffffff;
  box-shadow: 0 24px 60px rgba(25, 38, 56, 0.08);
}

.delivery-note-sign__topbar {
  display: flex;
  align-items: center;
  min-height: 92px;
  padding: 22px 36px;
  border-bottom: 1px solid #e8edf4;
}

.delivery-note-sign__brand {
  display: block;
  width: auto;
  height: 54px;
  object-fit: contain;
}

.delivery-note-sign__body {
  padding: 34px 40px 38px;
}

.delivery-note-sign__hero {
  display: flex;
  align-items: center;
  gap: 28px;
  margin-bottom: 28px;
}

.delivery-note-sign__hero-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 104px;
  height: 104px;
  flex: 0 0 104px;
  border-radius: 50%;
  background: radial-gradient(circle at top left, #f2fbf4, #d8eddc);
  color: #166b2f;
  font-size: 62px;
  box-shadow: 0 18px 34px rgba(31, 141, 58, 0.08),
    inset 0 0 0 1px rgba(31, 141, 58, 0.14);
}

.delivery-note-sign__hero-copy {
  min-width: 0;
}

.delivery-note-sign__hero-icon i,
.delivery-note-sign__summary-icon i,
.delivery-note-sign__item-icon i,
.delivery-note-sign__state-icon i,
.delivery-note-sign__placeholder-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1em;
  height: 1em;
  line-height: 1;
  transform: translateY(1px);
}

.delivery-note-sign__hero-icon i {
  color: #166b2f;
  font-size: 68px;
}

.delivery-note-sign__summary-icon i {
  color: #166b2f;
  font-size: 30px;
}

.delivery-note-sign__item-icon i {
  color: #166b2f;
  font-size: 30px;
}

.delivery-note-sign__title {
  margin: 0 0 10px;
  color: #152238;
  font-size: 34px;
  font-weight: 800;
  line-height: 1.15;
}

.delivery-note-sign__subtitle {
  margin: 0;
  color: #5d6b7f;
  font-size: 17px;
  line-height: 1.6;
}

.delivery-note-sign__summary {
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) 30px minmax(0, 0.95fr) 30px minmax(
      0,
      1.3fr
    );
  gap: 0;
  align-items: center;
  margin-bottom: 22px;
  padding: 22px 26px;
  border: 1px solid #e4eaf1;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(20, 35, 54, 0.04);
}

.delivery-note-sign__summary-item {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
  padding: 0 12px 0 4px;
}

.delivery-note-sign__summary-item--customer {
  align-items: flex-start;
}

.delivery-note-sign__summary-divider + .delivery-note-sign__summary-item {
  padding-left: 12px;
}

.delivery-note-sign__summary-divider {
  justify-self: center;
  width: 1px;
  min-height: 62px;
  background: #e4eaf1;
}

.delivery-note-sign__summary-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  flex: 0 0 52px;
  border-radius: 12px;
  background: linear-gradient(180deg, #eff9f1 0%, #dff0e3 100%);
  color: #166b2f;
  font-size: 28px;
  box-shadow: 0 8px 18px rgba(31, 141, 58, 0.07),
    inset 0 0 0 1px rgba(31, 141, 58, 0.14);
}

.delivery-note-sign__summary-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.delivery-note-sign__summary-label {
  margin-bottom: 4px;
  color: #5d6b7f;
  font-size: 14px;
}

.delivery-note-sign__summary-value {
  overflow: hidden;
  color: #152238;
  font-size: 17px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.delivery-note-sign__summary-value--customer {
  overflow: visible;
  text-overflow: clip;
  white-space: normal;
  word-break: break-word;
  line-height: 1.35;
}

.delivery-note-sign__section {
  margin-top: 18px;
  padding: 20px;
  border: 1px solid #e4eaf1;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(20, 35, 54, 0.04);
}

.delivery-note-sign__section-head {
  margin-bottom: 8px;
}

.delivery-note-sign__section-title {
  margin: 0;
  color: #152238;
  font-size: 19px;
  font-weight: 800;
}

.delivery-note-sign__table {
  overflow: hidden;
}

.delivery-note-sign__table-head,
.delivery-note-sign__table-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 120px;
  gap: 20px;
  align-items: center;
}

.delivery-note-sign__table-head {
  padding: 14px 0 16px;
  border-bottom: 1px solid #e4eaf1;
  color: #637188;
  font-size: 14px;
  font-weight: 700;
}

.delivery-note-sign__table-row {
  min-height: 68px;
  padding: 10px 0;
  border-bottom: 1px solid #edf1f5;
}

.delivery-note-sign__table-row:last-child {
  border-bottom: 0;
}

.delivery-note-sign__table-cell {
  color: #172338;
  font-size: 17px;
}

.delivery-note-sign__table-cell--article {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}

.delivery-note-sign__item-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.delivery-note-sign__item-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}

.delivery-note-sign__item-number {
  color: #637188;
  font-size: 13px;
}

.delivery-note-sign__table-cell--quantity {
  font-weight: 600;
  text-align: left;
}

.delivery-note-sign__item-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  border-radius: 12px;
  background: linear-gradient(180deg, #eff9f1 0%, #dff0e3 100%);
  color: #166b2f;
  font-size: 27px;
  box-shadow: 0 8px 18px rgba(31, 141, 58, 0.06),
    inset 0 0 0 1px rgba(31, 141, 58, 0.14);
}

.delivery-note-sign__signature-surface {
  position: relative;
  min-height: 220px;
  margin-top: 12px;
  border: 2px dashed #b9c9da;
  border-radius: 14px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  box-shadow: inset 0 0 0 1px rgba(31, 141, 58, 0.05),
    0 12px 24px rgba(20, 35, 54, 0.04);
  overflow: hidden;
}

.delivery-note-sign__signature-surface--signed {
  border-color: #87c88f;
  box-shadow: inset 0 0 0 1px rgba(31, 141, 58, 0.08),
    0 14px 28px rgba(31, 141, 58, 0.06);
}

.delivery-note-sign__signature-surface::before {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(
    circle at top left,
    rgba(31, 141, 58, 0.06),
    transparent 45%
  );
  pointer-events: none;
}

.delivery-note-sign__signature-placeholder {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #5d6b7f;
  pointer-events: none;
  text-align: center;
}

.delivery-note-sign__placeholder-icon {
  color: #1f8d3a;
  font-size: 48px;
  line-height: 1;
}

.delivery-note-sign__signature-placeholder strong {
  color: #2f3f58;
  font-size: 18px;
}

.delivery-note-sign__signature-placeholder span {
  color: #617089;
  font-size: 15px;
}

.delivery-note-sign__signature-pad {
  position: relative;
  z-index: 2;
}

.delivery-note-sign__section-text {
  margin: 10px 0 0;
  color: #5d6b7f;
  font-size: 15px;
  line-height: 1.6;
}

.delivery-note-sign__section--signature {
  margin-bottom: 30px;
}

.delivery-note-sign__action-dock {
  margin-top: 8px;
  padding-top: 18px;
  border-top: 1px solid #e4eaf1;
}

.delivery-note-sign__confirmation-note {
  margin: 0 0 12px;
  color: #516078;
  font-size: 14px;
  line-height: 1.55;
}

.delivery-note-sign__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.delivery-note-sign__clear-btn {
  min-width: 190px !important;
  min-height: 52px !important;
  border-color: #d9e0e8 !important;
  border-radius: 12px !important;
  color: #5a6980 !important;
  font-size: 15px !important;
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
}

.delivery-note-sign__submit-btn {
  min-width: 308px !important;
  min-height: 52px !important;
  border-radius: 12px !important;
  font-size: 15px !important;
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
}

.delivery-note-sign__submit-icon {
  margin-left: 10px;
  display: inline-flex !important;
  align-items: center;
  font-size: 21px;
  line-height: 1;
  color: #ffffff !important;
}

.delivery-note-sign__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 520px;
  gap: 20px;
  text-align: center;
}

.delivery-note-sign__state--error .delivery-note-sign__state-icon {
  background: radial-gradient(circle at 30% 30%, #fff3ef, #ffe2dc);
  color: #ca3a2f;
  box-shadow: 0 18px 36px rgba(202, 58, 47, 0.14),
    inset 0 0 0 1px rgba(202, 58, 47, 0.08);
}

.delivery-note-sign__state--success .delivery-note-sign__state-icon {
  background: radial-gradient(circle at 30% 30%, #effaf1, #daf0de);
  color: #1f8d3a;
  box-shadow: 0 18px 36px rgba(31, 141, 58, 0.14),
    inset 0 0 0 1px rgba(31, 141, 58, 0.08);
}

.delivery-note-sign__state-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 152px;
  height: 152px;
  border-radius: 50%;
}

.delivery-note-sign__state-icon::after {
  content: "";
  position: absolute;
  inset: 17px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.62);
}

.delivery-note-sign__state-symbol {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  line-height: 1;
}

.delivery-note-sign__state-symbol::before,
.delivery-note-sign__state-symbol::after {
  content: "";
  position: absolute;
  background: currentColor;
}

.delivery-note-sign__state-symbol--error::before {
  top: 10px;
  left: 50%;
  width: 16px;
  height: 42px;
  border-radius: 999px;
  transform: translateX(-50%);
}

.delivery-note-sign__state-symbol--error::after {
  bottom: 10px;
  left: 50%;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  transform: translateX(-50%);
}

.delivery-note-sign__state-symbol--success::before {
  width: 28px;
  height: 54px;
  border-right: 10px solid currentColor;
  border-bottom: 10px solid currentColor;
  background: transparent;
  transform: rotate(45deg) translate(-2px, -6px);
}

.delivery-note-sign__state-symbol--success::after {
  display: none;
}

.delivery-note-sign__state-title {
  margin: 0;
  color: #152238;
  font-size: 34px;
  font-weight: 800;
  line-height: 1.1;
}

.delivery-note-sign__state-text {
  max-width: 620px;
  margin: 0;
  color: #5d6b7f;
  font-size: 18px;
  line-height: 1.65;
}

@media (max-width: 960px) {
  .delivery-note-sign__body {
    padding: 28px 24px 30px;
  }

  .delivery-note-sign__hero {
    gap: 20px;
  }

  .delivery-note-sign__hero-icon {
    width: 84px;
    height: 84px;
    flex-basis: 84px;
    font-size: 44px;
  }

  .delivery-note-sign__hero-icon i {
    font-size: 54px;
  }

  .delivery-note-sign__title {
    font-size: 29px;
  }

  .delivery-note-sign__state-icon {
    width: 132px;
    height: 132px;
  }

  .delivery-note-sign__state-symbol {
    width: 72px;
    height: 72px;
  }

  .delivery-note-sign__summary {
    grid-template-columns: 1fr;
    gap: 18px;
  }

  .delivery-note-sign__summary-divider {
    display: none;
  }

  .delivery-note-sign__summary-item {
    padding-right: 4px;
  }

  .delivery-note-sign__summary-divider + .delivery-note-sign__summary-item {
    padding-left: 4px;
  }
}

@media (max-width: 767px) {
  .delivery-note-sign__page {
    padding: 16px 12px;
  }

  .delivery-note-sign__panel {
    border-radius: 18px;
  }

  .delivery-note-sign__topbar {
    min-height: 72px;
    padding: 16px 18px;
  }

  .delivery-note-sign__brand {
    height: 44px;
  }

  .delivery-note-sign__body {
    padding: 22px 16px 24px;
  }

  .delivery-note-sign__hero {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .delivery-note-sign__hero-icon {
    width: 76px;
    height: 76px;
    flex-basis: 76px;
    font-size: 40px;
  }

  .delivery-note-sign__title {
    font-size: 26px;
  }

  .delivery-note-sign__subtitle {
    font-size: 16px;
  }

  .delivery-note-sign__state-icon {
    width: 116px;
    height: 116px;
  }

  .delivery-note-sign__state-icon::after {
    inset: 13px;
  }

  .delivery-note-sign__state-symbol {
    width: 62px;
    height: 62px;
  }

  .delivery-note-sign__state-title {
    font-size: 28px;
  }

  .delivery-note-sign__state-text {
    font-size: 16px;
  }

  .delivery-note-sign__table-head,
  .delivery-note-sign__table-row {
    grid-template-columns: minmax(0, 1fr) 74px;
    gap: 12px;
  }

  .delivery-note-sign__table-cell,
  .delivery-note-sign__item-name {
    font-size: 15px;
    white-space: normal;
  }

  .delivery-note-sign__item-icon {
    width: 40px;
    height: 40px;
    flex-basis: 40px;
    font-size: 22px;
  }

  .delivery-note-sign__action-dock {
    padding-top: 16px;
  }

  .delivery-note-sign__confirmation-note {
    font-size: 13px;
  }

  .delivery-note-sign__actions {
    flex-direction: column;
    align-items: stretch;
  }

  .delivery-note-sign__clear-btn,
  .delivery-note-sign__submit-btn {
    width: 100%;
    min-width: 0 !important;
  }

  .delivery-note-sign__state {
    min-height: 420px;
  }
}
</style>
